import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissionChecker } from "@/hooks/shared/use-permission-checker";
import { useClientProfile } from "@/contexts/client-profile-context";
import type { RoutePermissions } from "@/routes/config";
// Directo e não pelo barril `@/pages/auth`: o barril traz o reset de palavra-passe, que traz
// todos os schemas Zod da API (≈500 KB) para o chunk de arranque.
import { AccessDeniedPage } from "@/pages/auth/access-denied-page";
import { LoadingPage } from "@/components/ui/loading-page";
import { anyModuleOfPath, moduleOfPath } from "@/lib/menu-paths";
import { ROUTES } from "@/routes/paths";

interface ProtectedRouteProps {
  children: ReactNode;
  permissions: RoutePermissions;
}

/** Os únicos endereços que uma conta de posto pode abrir — a app dela e mais nada. */
const SECURITY_ACCOUNT_PATHS: string[] = [
  ROUTES.SECURITY,
  ROUTES.SECURITY_GATE,
  ROUTES.SECURITY_INCIDENT,
  ROUTES.SECURITY_LOG,
];

/**
 * Route guard that checks permissions before rendering.
 * Uses usePermissionChecker as the single source of truth.
 */
export const ProtectedRoute = ({ children, permissions }: ProtectedRouteProps) => {
  const { isLoading, hasPermission, user } = usePermissionChecker();
  const { hasFeature, hasModule, hasModuleForSection, isLoading: perfilPorCarregar } = useClientProfile();
  const location = useLocation();

  if (isLoading) {
    return <LoadingPage message="A autenticar…" />;
  }

  // ESPERAR PELO PERFIL antes de decidir por módulo ou feature.
  //
  // Enquanto `/config/profile` não responde vale o `DEFAULT_PROFILE`, que só
  // tem os módulos do núcleo e nenhuma feature. Decidir aí barra rotas que o
  // cliente comprou: os dois gates abaixo disparam, e o `Navigate` é `replace`
  // — quando o perfil chega já não há para onde voltar. Acontecia a quem
  // abrisse o endereço directamente ou recarregasse a página; pelo menu não,
  // porque o perfil já lá estava.
  if (perfilPorCarregar) {
    return <LoadingPage message="A carregar…" />;
  }

  // Uma rota pública pode na mesma pertencer a uma feature que só alguns
  // clientes têm — o recrutamento é o caso: as páginas são abertas a quem não
  // tem conta, mas só existem no cliente que recruta. Sem sessão não há
  // dashboard para onde voltar, por isso vai para a raiz.
  if (permissions.access === "public") {
    if (permissions.feature && !hasFeature(permissions.feature)) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // GATE DE MÓDULO — o módulo que cobre esta rota não foi comprado.
  //
  // Esconder a entrada no menu não chega: quem escreve o endereço à mão entra
  // à mesma. Foi o que aconteceu — `/dashboard/hr/dashboard` abria num cliente
  // sem Recursos Humanos, apesar de a secção não aparecer no menu.
  //
  // A API já responde 404 a essas rotas, portanto a página abria vazia ou com
  // erros. Isto fecha-a no sítio certo: antes de renderizar.
  //
  // O módulo é derivado do CAMINHO (ver `moduleOfPath`), não de uma anotação
  // por rota — uma rota nova fica coberta sem ninguém se lembrar de a marcar.
  //
  // Uma rota que aceita "qualquer um destes módulos" (o Calendário) decide por
  // essa regra e não pelo módulo único do caminho.
  const algumDosModulos = anyModuleOfPath(location.pathname);
  const moduloDaRota = moduleOfPath(location.pathname);
  if (algumDosModulos) {
    if (!algumDosModulos.some((m) => hasModule(m))) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
  } else if (moduloDaRota && !hasModuleForSection(moduloDaRota)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Feature-flag do perfil desligada (ex.: Banco de Horas na Afisocks) → a rota
  // não existe para este cliente; devolve ao dashboard.
  if (permissions.feature && !hasFeature(permissions.feature)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Conta de posto de segurança: qualquer rota que não seja a app dela devolve-a
  // à app dela. Tem de ser AQUI (e não no layout) porque o "Acesso Negado" é
  // renderizado por este guarda — e essa página traz um botão "Voltar ao
  // dashboard", ou seja, mostrava-lhe a porta da app da empresa. A conta nunca
  // deve ver sequer que a empresa existe.
  //
  // A lista é dos QUATRO separadores da app, não só da raiz: desde que a barra
  // de baixo passou a navegar por endereço, deixar só a raiz aqui fazia o
  // guarda expulsar o segurança da própria barra — tocava em "Portaria" e era
  // devolvido a "Hoje". Continua a ser uma lista fechada, e é de propósito:
  // acrescentar um ecrã à app do posto é uma decisão que se toma aqui.
  if (user?.isSecurityAccount && !SECURITY_ACCOUNT_PATHS.includes(location.pathname)) {
    return <Navigate to={ROUTES.SECURITY} replace />;
  }

  if (!hasPermission(permissions)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
};
